import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiPlus,
  FiEdit3,
  FiUsers,
  FiMessageSquare,
  FiClock,
  FiPhone,
  FiImage,
  FiLoader,
  FiShoppingBag,
} from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import {
  productInstanceIds,
  productUnlocksConsulting,
  isProductFreeForJoin,
  purchasedProductIds,
} from "../utils/mintSpaceApps";
import { ensureConsultingEngagement } from "../api/mintSpaceConsulting";
import ConsultingBookSlotModal from "./ConsultingBookSlotModal";
import { getMintSpaceAffiliateProgram, saveMintSpaceAffiliateProgram } from "../api/affiliate";

const SYMBOL = "₾";
const PERIOD_WORD = {
  "1w": "week",
  "1m": "month",
  "3m": "3 months",
  "6m": "6 months",
  "1y": "year",
};

const accessSummary = (product, t) => {
  if ((product.consultingBookingFlow || "book_then_pay") === "book_then_pay") {
    const min = product.consultingSlotMinutes || 45;
    return t("consulting.accessSummaryBook", { min }) || `${min} min · pick a time, then pay`;
  }
  const mode = product.consultingAccessMode || "time";
  if (mode === "quota") {
    const n = product.consultingCallQuota || 1;
    return t("consulting.accessSummaryQuota", { n }) || `${n} included call${n === 1 ? "" : "s"}`;
  }
  if (mode === "manual") {
    return t("consulting.accessSummaryManual") || "Chat & calls until you close access";
  }
  const days = product.consultingDurationDays || 30;
  return t("consulting.accessSummaryTime", { days }) || `${days} days of chat & calls`;
};

/**
 * Catalog of consulting products inside the Consulting app — owner manages offerings;
 * buyers browse and purchase before opening a session.
 */
export default function ConsultingOfferingsPanel({
  isDark,
  setToast,
  mintSpace,
  appInstanceId,
  isAdmin,
  engagements = [],
  onCreateOffering,
  onEditOffering,
  onOpenAffiliate,
  onOpenSession,
  onEngagementsChange,
}) {
  const { t } = useTranslation();
  const spaceId = mintSpace?._id;
  const bought = useMemo(() => purchasedProductIds(mintSpace), [mintSpace]);

  const offerings = useMemo(() => {
    if (!mintSpace?.appPricing?.length || !appInstanceId) return [];
    const inst = String(appInstanceId);
    return mintSpace.appPricing.filter(
      (p) =>
        productUnlocksConsulting(p) &&
        productInstanceIds(p, mintSpace).some((id) => String(id) === inst)
    );
  }, [mintSpace, appInstanceId]);

  const [affiliateByProduct, setAffiliateByProduct] = useState({});
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const loadAffiliateFlags = useCallback(async () => {
    if (!isAdmin || !spaceId || !offerings.length) return;
    setAffiliateLoading(true);
    try {
      const entries = await Promise.all(
        offerings.map(async (p) => {
          try {
            const res = await getMintSpaceAffiliateProgram(spaceId, p._id);
            return [String(p._id), !!res?.data?.enabled];
          } catch {
            return [String(p._id), false];
          }
        })
      );
      setAffiliateByProduct(Object.fromEntries(entries));
    } finally {
      setAffiliateLoading(false);
    }
  }, [isAdmin, spaceId, offerings]);

  useEffect(() => {
    loadAffiliateFlags();
  }, [loadAffiliateFlags]);

  const toggleAffiliate = async (product) => {
    if (!spaceId || !product?._id) return;
    const pid = String(product._id);
    const next = !affiliateByProduct[pid];
    setTogglingId(pid);
    try {
      await saveMintSpaceAffiliateProgram(spaceId, {
        productId: pid,
        enabled: next,
        commissionType: "percentage",
        commissionRate: 30,
        approvalMode: "auto",
        visibility: "public",
      });
      setAffiliateByProduct((prev) => ({ ...prev, [pid]: next }));
      setToast?.(
        next
          ? t("consulting.affiliateEnabled") || "Affiliates can promote this offering"
          : t("consulting.affiliateDisabled") || "Affiliate program turned off"
      );
    } catch (err) {
      setToast?.(err?.message || t("consulting.affiliateSaveFailed") || "Could not update affiliate");
    } finally {
      setTogglingId(null);
    }
  };

  const baseText = "text-label-primary";
  const muted = "text-label-secondary";
  const card = "border-separator-default bg-bg-raised";
  const mediaBg = "bg-bg-raised";
  const chip = isDark ? "bg-white/10 text-slate-200" : "bg-slate-100 text-slate-700";

  const engagementForProduct = (productId) =>
    engagements.find((e) => String(e.product) === String(productId));

  const hasProductAccess = (product) => {
    const pid = String(product._id);
    if (bought.has(pid)) return true;
    if (isProductFreeForJoin(product) && (mintSpace?.isMember || mintSpace?.isOwner)) return true;
    return false;
  };

  const [openingProductId, setOpeningProductId] = useState(null);
  const [bookProduct, setBookProduct] = useState(null);

  const openSessionForProduct = async (product) => {
    if (!spaceId || !appInstanceId || !product?._id) return;
    let eng = engagementForProduct(product._id);
    if (!eng) {
      setOpeningProductId(String(product._id));
      try {
        const res = await ensureConsultingEngagement(spaceId, {
          productId: product._id,
          instanceId: appInstanceId,
        });
        eng = res?.data?.engagement;
        if (eng) onEngagementsChange?.();
      } catch (err) {
        setToast?.(err.response?.data?.message || err.message || "Could not open session");
        return;
      } finally {
        setOpeningProductId(null);
      }
    }
    if (eng?._id) onOpenSession?.(eng._id);
  };

  if (!offerings.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-10 text-center">
        <div
          className={`mb-4 flex h-14 w-14 items-center justify-center rounded-radius-md bg-app-consulting-base text-app-consulting-on`}
        >
          <FiMessageSquare className="h-7 w-7" />
        </div>
        <h3 className={`text-title-3 ${baseText}`}>
          {isAdmin
            ? t("consulting.noOfferingsOwnerTitle") || "Create your first consultation"
            : t("consulting.noOfferingsBuyerTitle") || "No consultations available yet"}
        </h3>
        <p className={`mt-2 max-w-sm text-subheadline leading-relaxed ${muted}`}>
          {isAdmin
            ? t("consulting.noOfferingsOwnerHint") ||
              "Add a priced offering with access rules — buyers get private chat and video calls after checkout."
            : t("consulting.noOfferingsBuyerHint") ||
              "When the owner publishes offerings, they will show up here to purchase."}
        </p>
        {isAdmin && onCreateOffering && (
          <button
            type="button"
            onClick={onCreateOffering}
            className="mt-6 inline-flex items-center gap-2 rounded-radius-sm bg-accent-default px-5 py-2.5 text-subheadline-semibold text-accent-on transition hover:opacity-95"
          >
            <FiPlus className="h-4 w-4" />
            {t("consulting.addOffering") || "Add consultation"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div
        className={`flex shrink-0 flex-wrap items-center justify-between gap-3 border-b px-4 py-3 ${
          "border-separator-default"
        }`}
      >
        <div>
          <p className={`text-subheadline-semibold ${baseText}`}>
            {isAdmin
              ? t("consulting.offeringsTitleOwner") || "Your consultations"
              : t("consulting.offeringsTitleBuyer") || "Book a consultation"}
          </p>
          <p className={`text-footnote ${muted}`}>
            {isAdmin
              ? t("consulting.offeringsHintOwner") ||
                "Each offering is a product with its own price and access rules."
              : t("consulting.offeringsHintBuyer") ||
                "Choose an available time, pay, then join chat and video for your session."}
          </p>
        </div>
        {isAdmin && onCreateOffering && (
          <button
            type="button"
            onClick={onCreateOffering}
            className="inline-flex items-center gap-1.5 rounded-radius-sm bg-accent-default px-3.5 py-2 text-footnote-semibold text-accent-on transition hover:opacity-90"
          >
            <FiPlus className="h-3.5 w-3.5" />
            {t("consulting.addOffering") || "Add consultation"}
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4 campaign-window-scrollbar">
        <div className="mx-auto grid max-w-4xl grid-cols-1 gap-3 sm:grid-cols-2">
          {offerings.map((product) => {
            const owned = hasProductAccess(product);
            const engagement = engagementForProduct(product._id);
            const isFree = isProductFreeForJoin(product);
            const periodWord =
              product.billingType === "recurring" ? PERIOD_WORD[product.period] || "month" : "";
            const priceLabel = isFree
              ? t("mintSpace.free") || "Free"
              : `${SYMBOL}${product.price ?? 0}${periodWord ? ` / ${periodWord}` : ""}`;
            const mediaSrc = product.listingBanner || product.bannerImage || "";
            const mediaType = product.listingBanner
              ? product.listingBannerType || "image"
              : product.bannerType || "image";
            const pid = String(product._id);
            const affiliateOn = !!affiliateByProduct[pid];

            return (
              <article
                key={product._id}
                className={`overflow-hidden rounded-radius-md border shadow-sm transition hover:border-app-consulting-base ${card}`}
              >
                <div className={`relative aspect-[16/10] w-full overflow-hidden ${mediaBg}`}>
                  {mediaSrc ? (
                    mediaType === "video" ? (
                      <video
                        src={mediaSrc}
                        className="h-full w-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img src={mediaSrc} alt="" className="h-full w-full object-cover" />
                    )
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <FiImage className={`h-10 w-10 opacity-30 ${muted}`} />
                    </div>
                  )}
                  <span className="absolute bottom-2 left-2 rounded-radius-xs bg-black/55 px-2 py-0.5 text-caption-1-semibold text-white backdrop-blur-sm">
                    {priceLabel}
                  </span>
                  {affiliateOn && isAdmin && (
                    <span className="absolute right-2 top-2 rounded-radius-xs bg-mint/90 px-2 py-0.5 text-caption-1-semibold text-accent-on">
                      {t("consulting.affiliateBadge") || "Affiliate"}
                    </span>
                  )}
                </div>

                <div className="p-3.5">
                  <h4 className={`truncate text-subheadline-semibold ${baseText}`}>
                    {product.name || t("mintSpace.untitledProduct") || "Untitled"}
                  </h4>
                  {product.headline && (
                    <p className={`mt-0.5 line-clamp-2 text-footnote leading-snug ${muted}`}>{product.headline}</p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption-1 font-medium ${chip}`}>
                      <FiClock className="h-3 w-3 opacity-70" />
                      {accessSummary(product, t)}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption-1 font-medium ${chip}`}>
                      <FiMessageSquare className="h-3 w-3 opacity-70" />
                      {t("consulting.includesChat") || "Private chat"}
                    </span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-caption-1 font-medium ${chip}`}>
                      <FiPhone className="h-3 w-3 opacity-70" />
                      {t("consulting.includesCalls") || "Video calls"}
                    </span>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {isAdmin ? (
                      <>
                        <button
                          type="button"
                          onClick={() => onEditOffering?.(product._id)}
                          className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-radius-sm border px-3 py-2 text-footnote-semibold transition ${
                            isDark
                              ? "border-white/10 hover:border-accent-default hover:text-accent-text"
                              : "border-slate-200 hover:border-accent-default hover:text-status-success"
                          }`}
                        >
                          <FiEdit3 className="h-3.5 w-3.5" />
                          {t("common.edit") || "Edit"}
                        </button>
                        <button
                          type="button"
                          disabled={affiliateLoading || togglingId === pid}
                          onClick={() =>
                            onOpenAffiliate ? onOpenAffiliate(product._id) : toggleAffiliate(product)
                          }
                          className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-radius-sm border px-3 py-2 text-footnote-semibold transition ${
                            affiliateOn
                              ? "border-accent-default bg-accent-tinted text-accent-text"
                              : isDark
                                ? "border-white/10 hover:border-app-consulting-base"
                                : "border-slate-200 hover:border-app-consulting-base"
                          }`}
                        >
                          {togglingId === pid ? (
                            <FiLoader className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <FiUsers className="h-3.5 w-3.5" />
                          )}
                          {affiliateOn
                            ? t("consulting.affiliateOn") || "Affiliate on"
                            : t("consulting.enableAffiliate") || "Enable affiliate"}
                        </button>
                      </>
                    ) : owned ? (
                      <button
                        type="button"
                        disabled={openingProductId === pid}
                        onClick={() => openSessionForProduct(product)}
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-radius-sm bg-app-consulting-base px-3 py-2.5 text-footnote-semibold text-app-consulting-on disabled:opacity-60"
                      >
                        {openingProductId === pid ? (
                          <FiLoader className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <FiMessageSquare className="h-3.5 w-3.5" />
                        )}
                        {t("consulting.openSession") || "Open chat & calls"}
                      </button>
                    ) : (product.consultingBookingFlow || "book_then_pay") === "book_then_pay" ? (
                      <button
                        type="button"
                        onClick={() => setBookProduct(product)}
                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-radius-sm bg-accent-default px-3 py-2.5 text-footnote-semibold text-accent-on transition hover:opacity-95"
                      >
                        <FiClock className="h-3.5 w-3.5" />
                        {t("consulting.pickTimeAndPay") || "Pick time & pay"}
                      </button>
                    ) : (
                      <div className="flex w-full flex-col gap-2">
                        <Link
                          to={`/mint-space/${spaceId}/product/${product._id}`}
                          className="inline-flex w-full items-center justify-center gap-1.5 rounded-radius-sm bg-accent-default px-3 py-2.5 text-footnote-semibold text-accent-on transition hover:opacity-95"
                        >
                          <FiShoppingBag className="h-3.5 w-3.5" />
                          {isFree
                            ? t("consulting.getFreeAccess") || "Get access"
                            : t("consulting.viewAndBuy") || "View & purchase"}
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
      {bookProduct && (
        <ConsultingBookSlotModal
          isDark={isDark}
          setToast={setToast}
          mintSpaceId={spaceId}
          product={bookProduct}
          onClose={() => setBookProduct(null)}
          onPaid={() => {
            setBookProduct(null);
            onEngagementsChange?.();
          }}
        />
      )}
    </div>
  );
}
